<template>
  <div class="chat-page">
    <div class="chat-header">
      <h1 class="chat-title">智能助手</h1>
      <div class="service-status" :class="{ 'status-online': ollamaServiceAvailable }">
        {{ ollamaServiceAvailable ? '服务在线' : '服务离线' }}
      </div>
    </div>

    <div class="chat-container" :class="{ 'chat-active': messages.length > 0 }">
      <div class="chat-sidebar-toggle" @click="toggleSidebar">
        <i class="el-icon-menu"></i>
      </div>

      <transition name="slide">
        <div class="chat-sidebar" v-if="showSidebar">
          <div class="sidebar-header">
            <h3>历史会话</h3>
            <button class="new-chat-btn" @click="startNewChat">
              <i class="el-icon-plus"></i> 新对话
            </button>
          </div>
          <div class="history-list">
            <div class="history-empty" v-if="!hasHistory">
              暂无历史记录
            </div>
            <!-- 这里可以添加历史对话列表 -->
          </div>
        </div>
      </transition>

      <div class="chat-main">
        <ChatArea
          :messages="messages"
          :loading="loading"
          @suggest-question="handleSuggestedQuestion"
        />

        <div class="chat-bottom">
          <div class="model-info" v-if="ollamaServiceAvailable">
            使用模型: {{ modelInfo }}
          </div>

          <MessageInput
            @send-message="sendMessage"
            :disabled="loading"
            :has-messages="messages.length > 0"
            @send-test="testConnection"
          />

          <div class="chat-disclaimer">
            AI可能会产生错误信息，请以教材内容为准
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatArea from '@/views/ai/components/ChatArea.vue'
import LoginModal from '@/views/ai/components/LoginModal.vue'
import MessageInput from '@/views/ai/components/MessageInput.vue'
import Sidebar from '@/views/ai/components/Sidebar.vue'
import { sendChatMessage, sendTestMessage } from '@/views/ai/service/api'
import { checkOllamaService } from '@/views/ai/service/ollamaService'

export default {
  components: {
    ChatArea,
    MessageInput
  },
  data() {
    return {
      // UI 状态
      showLoginModal: false,
      loading: false,
      ollamaServiceAvailable: false,
      showSidebar: false,

      // 用户选择
      selectedGrade: '一年级',
      selectedSubject: '语文',

      // 聊天数据
      messages: [],
      hasHistory: false,
      modelInfo: 'deepseek-r1:1.5b',

      // 下拉选项
      grades: ['一年级', '二年级', '三年级', '四年级', '五年级', '六年级'],
      subjects: ['语文', '数学', '英语']
    }
  },
  mounted() {
    // 检查Ollama服务状态
    this.checkServiceStatus()
  },
  methods: {
    // 检查Ollama服务状态
    async checkServiceStatus() {
      try {
        this.ollamaServiceAvailable = await checkOllamaService()
        console.log('Ollama服务状态:', this.ollamaServiceAvailable ? '在线' : '离线')
      } catch (error) {
        console.error('检查服务状态失败:', error)
        this.ollamaServiceAvailable = false
      }
    },

    // 测试API连接
    async testConnection() {
      if (this.loading) return

      this.loading = true
      try {
        const result = await sendTestMessage()
        if (result.success) {
          this.messages.push({
            role: 'assistant',
            content: `连接测试成功: ${result.message}`,
            isSystem: true
          })
        }
      } catch (error) {
        this.messages.push({
          role: 'assistant',
          content: `连接测试失败: ${error.message}`,
          isError: true,
          isSystem: true
        })
      } finally {
        this.loading = false
      }
    },

    // 处理建议问题
    handleSuggestedQuestion(question) {
      this.sendMessage(question)
    },

    // 切换侧边栏
    toggleSidebar() {
      this.showSidebar = !this.showSidebar
    },

    // 开始新对话
    startNewChat() {
      this.messages = []
      this.showSidebar = false
    },

    async sendMessage(content) {
      if (!content.trim()) return

      // 添加用户消息
      this.messages.push({
        role: 'user',
        content
      })

      this.loading = true

      try {
        // 调用API服务发送消息
        const aiResponse = await sendChatMessage(this.messages)

        // 添加 AI 回复
        this.messages.push({
          role: 'assistant',
          content: aiResponse
        })
      } catch (error) {
        console.error('API 请求失败:', error)

        // 添加错误消息
        this.messages.push({
          role: 'assistant',
          content: `抱歉，发生了错误: ${error.message || '请稍后再试'}`,
          isError: true
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.chat-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f9fafb;
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.chat-title {
  font-size: 22px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.service-status {
  font-size: 14px;
  padding: 4px 10px;
  border-radius: 12px;
  background: #fee2e2;
  color: #b91c1c;
}

.status-online {
  background: #dcfce7;
  color: #15803d;
}

.chat-container {
  display: flex;
  flex: 1;
  position: relative;
  overflow: hidden;
}

.chat-sidebar-toggle {
  position: absolute;
  top: 16px;
  left: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}

.chat-sidebar-toggle:hover {
  background: #f3f4f6;
}

.chat-sidebar {
  width: 280px;
  background: white;
  border-right: 1px solid #e5e7eb;
  height: 100%;
  overflow-y: auto;
  padding: 16px;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.05);
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.sidebar-header h3 {
  margin: 0;
  font-size: 16px;
  color: #1f2937;
}

.new-chat-btn {
  background: #2563eb;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}

.history-list {
  margin-top: 16px;
}

.history-empty {
  color: #9ca3af;
  font-size: 14px;
  text-align: center;
  padding: 24px 0;
}

.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px;
  overflow: hidden;
}

.chat-bottom {
  margin-top: 16px;
}

.model-info {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
  text-align: center;
}

.chat-disclaimer {
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
  margin-top: 8px;
}

.slide-enter-active, .slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from, .slide-leave-to {
  transform: translateX(-100%);
}

.chat-active {
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0.7;
  }
  to {
    opacity: 1;
  }
}
</style>
